import React from 'react';
import {
    BranchFork16Regular,
    CalendarLtr16Regular,
    Star16Regular,
    Eye16Regular,
    Money16Regular,
    ArrowCircleDown16Regular,
    PeopleTeam16Regular,
    Person16Regular,
    LayerDiagonalAddRegular,
    EditRegular,
} from "@fluentui/react-icons";
import { Button } from '@fluentui/react-components';
import { ContractConcept } from "../../../../models/Entities/Contracts/ContractConcept";
import { useTranslation } from 'react-i18next';
import './ConceptList.Styles.css';
import { ConfirmationButtonComponent } from "../../../../commons/component/ConfirmationButton/ConfirmationButtonComponent";
import { ErrorType } from "../../../../models/HttpError";
import {
    getSettlementLabel,
    getValuationLabel,
} from '../ConceptGroupedView/ConceptGroupedView.Functions';


export interface IProps {
    Concept: ContractConcept;
    index: number;
    isDeleting: boolean;
    failonDeleting: boolean;
    error: ErrorType | undefined;
    onEdit?: (stage: ContractConcept) => void;
    onDelete?: (stage: ContractConcept) => void;
}


export const ConceptItemComponent = (props: IProps) => {
    const [t] = useTranslation();
    const cc = props.Concept;

    const isFixedValue = Number(cc.valuationType) === 1;
    const isPercentage = Number(cc.valuationType) === 3;
    const isByStage = Number(cc.valuationType) === 5;
    const currency = cc.conceptCurrency || '';

    const formatPrice = (v?: number) =>
        typeof v === 'number' && !isNaN(v)
            ? v.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
            : '';

    const showPrice = isFixedValue && typeof cc.priceValue === 'number' && !isNaN(cc.priceValue);
    const showPct = isPercentage && typeof cc.percentageValue === 'number' && !isNaN(cc.percentageValue);

    const executions: { key: string; label: string; active: boolean }[] = [
        { key: 'inFace', label: 'Presencial', active: !!cc.applyToInFaceExecution },
        { key: 'remote', label: 'Remoto',     active: !!cc.applyToRemoteExecution },
        { key: 'phone',  label: 'Telefónico', active: !!cc.applyToPhoneExecution },
    ];
    const activeExecutions = executions.filter(e => e.active);

    return (
        <div className="ci-card" key={cc.id}>
            {/* ── LEFT: main content ── */}
            <div className="ci-body">

                {/* Row 1: name + code */}
                <div className="ci-title-row">
                    <span className="ci-name">{cc.concept?.conceptName}</span>
                    {cc.concept?.conceptCode && (
                        <span className="ci-code">{cc.concept.conceptCode}</span>
                    )}
                </div>

                {/* Row 2: description */}
                {cc.concept?.conceptDescription && (
                    <p className="ci-desc">{cc.concept.conceptDescription}</p>
                )}

                {/* Row 3: settlement + valuation type + executions */}
                <div className="ci-meta-row">
                    <span className="ci-meta-pill">
                        {getSettlementLabel(cc.settlementSetting)}
                    </span>
                    <span className="ci-meta-divider">·</span>
                    <span className="ci-meta-pill">
                        {getValuationLabel(cc.valuationType)}
                    </span>
                    {activeExecutions.length > 0 && (
                        <>
                            <span className="ci-meta-divider">·</span>
                            <span className="ci-exec-group">
                                {activeExecutions.map(e => (
                                    <span
                                        key={e.key}
                                        className="ci-exec-pill"
                                        title={`Aplica: ${e.label}`}
                                    >{e.label}</span>
                                ))}
                            </span>
                        </>
                    )}
                </div>

                {/* Row 4: feature badges */}
                <div className="ci-badges">
                    {!!cc.groupWithInTheVisit && (
                        <span className="ci-badge ci-badge--group"><BranchFork16Regular />{t('conceptList.badges.groupByVisit', 'Agrupado')}</span>
                    )}
                    {isByStage && (
                        <span className="ci-badge ci-badge--visit"><CalendarLtr16Regular />{t('conceptList.badges.byVisit', 'Por visita')}</span>
                    )}
                    {cc.isIncludeInVisit && (
                        <span className="ci-badge ci-badge--includeInVisit"><Eye16Regular />{t('conceptList.badges.includeInVisit', 'Inc. en visita')}</span>
                    )}
                    {cc.isOptional && (
                        <span className="ci-badge ci-badge--optional"><Star16Regular />{t('conceptList.badges.optional', 'Opcional')}</span>
                    )}
                    {cc.isManagedByCoordinator && (
                        <span className="ci-badge ci-badge--coordinator"><Person16Regular />{t('conceptList.badges.coordinator', 'Coordinador')}</span>
                    )}
                    {cc.haveCost && (
                        <span className="ci-badge ci-badge--cost"><Money16Regular />{t('conceptList.badges.haveCost', 'Con costo')}</span>
                    )}
                    {cc.haveWithdrawal && (
                        <span className="ci-badge ci-badge--withdrawal"><ArrowCircleDown16Regular />{t('conceptList.badges.haveWithdrawal', 'Retención')}</span>
                    )}
                    {cc.haveDivisionFee && (
                        <span className="ci-badge ci-badge--division"><PeopleTeam16Regular />{t('conceptList.badges.divisionFee', 'Div. honorarios')}</span>
                    )}
                    {cc.haveOverhead && (
                        <span className="ci-badge ci-badge--overhead"><LayerDiagonalAddRegular />{t('conceptList.badges.overhead', 'Overhead')}</span>
                    )}
                </div>
            </div>

            {/* ── RIGHT: value + actions ── */}
            <div className="ci-aside">
                {showPrice && (
                    <div className="ci-value-chip">
                        <span className="ci-value-currency">{currency}</span>
                        <span className="ci-value-amount">{formatPrice(cc.priceValue)}</span>
                    </div>
                )}
                {showPct && (
                    <div className="ci-value-chip ci-value-chip--pct">
                        <span className="ci-value-amount">{cc.percentageValue}%</span>
                    </div>
                )}
                <div className="ci-actions">
                    <Button
                        appearance='subtle'
                        size='small'
                        icon={<EditRegular />}
                        aria-label='Editar'
                        onClick={() => props.onEdit?.(cc)}
                    />
                    <ConfirmationButtonComponent
                        ButtonId={"deleteconcept" + cc.id}
                        ButtonIconName={"Delete"}
                        ButtonText={""}
                        ButtonCssClass={""}
                        ConfirmationButtonText={t('conceptItem.confirmationButton.deleteButton')}
                        ConfirmationButtonCssClass={""}
                        CancelButtonText={t('conceptItem.confirmationButton.cancelButton')}
                        CancelButtonCssClass={""}
                        Title={t('conceptItem.confirmationButton.tittle')}
                        Description={""}
                        ElementTextHeader={t('conceptItem.confirmationButton.deleteConcept') + cc.concept?.conceptName}
                        ElementTextInformation={t('conceptItem.confirmationButton.textInformation')}
                        onConfirmation={() => props.onDelete?.(cc)}
                        onCancel={() => {}}
                        isOperationExecuting={props.isDeleting}
                        isOperationExecutedWithError={false}
                        OperationError={undefined}
                    />
                </div>
            </div>
        </div>
    );
};